import { CustomiserModal } from './customiser-modal';
import { videoContent } from './types';
import Vimeo from '@vimeo/player';

const getMarkup = (data:videoContent) => {
  const template = document.createElement('template');
  const topText =  `<div class="top-text"><span>Need help?</span><div class="line"></div></div>`;

  template.innerHTML = `
    <style>
      :host {
        display: block;
        text-align: center;
        padding: 3rem 1.25rem 0;
        background-color: #F5F2F0;
      }

      .top-text {
        font-size: 1rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.5rem;
        margin-bottom: 1.75rem;
        position: relative;
        color: #163021;
      }

      .top-text .line {
        height: .0625rem;
        width: 100%;
        background-color: rgba(22, 48, 33, 0.10);
        top: 50%;
        transform: translateY(-50%);
        position: absolute;
      }
      
      .top-text span {
        background-color: #F5F2F0;
        z-index: 1;
        display: block;
        position: relative;
        width: fit-content;
        margin: 0 auto;
        padding: 0 1.25rem;
      }

      .video-modal-button {
        display: flex;
        background: #FCFCFB;
        border-radius: 1.25rem;
        overflow: hidden;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
        min-height: 6rem;
      }

      .video-modal-button p {
        display: none;
      }

      .video-modal-button .image-wrapper {
        width: 9.5625rem;
        height: 6rem;
        position: relative;
      }

      .video-modal-button img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .title-wrapper {
        padding: 0 1rem 0 1.25rem;
        text-align: left;
        font-family: 'TTNorms Font', Helvectica, sans-serif;
        font-size: .875rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.25rem;
        display: flex;
        flex-flow: column;
        gap: .25rem;
        color: #163021;
      }

      .duration {
        color: #7C8D80;
      }

      .is-play {
        position: absolute;
        z-index: 1;
        height: 2.125rem;
        width: 2.125rem;
        border-radius: 50%;
        background: #FCFCFB;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }

      .is-play svg {
        width: 12px;
        height: 12px;
      }

      .is-play svg path {
        fill: #3F7355;
        stroke: #3F7355;
      }

      iframe {
        display: none;
      }

      @media only screen and (min-width: 1100px) {
        :host {
          height: 100%;
          display: block;
          text-align: center;
          background-color: #F5F2F0;
          font-size: 1rem;
          line-height: 1.5rem;
          grid-column: 1 / 2;
          padding: 3.125rem 0;
          width: calc(100vw - 40.75rem);
        }

        .video-modal-button {
          max-width: 37.0625rem;
        }

        .video-modal-button .image-wrapper {
          width: 8.375rem;
          height: 6.125rem;
        }

        .is-play {
          height: 2.5rem;
          width: 2.5rem;
        }
  
        .is-play svg {
          width: .875rem;
          height: .875rem;
        }

        .top-text {
          text-align: left;
          margin-bottom: 29px;
        }
        
        .top-text span {
          background-color: #F5F2F0;
          z-index: 1;
          display: block;
          position: relative;
          width: fit-content;
          margin: 0;
          padding: 0 1.875rem 0 0;
        }
      }

      @media only screen and (min-width: 1750px) {
        :host {
          width: calc(100vw - 56rem);
        }

        .top-text {
          font-size: 1.125rem;
          line-height: 1.5rem;
          
        }

        .title-wrapper {
          font-size: 1.125rem;
          line-height: 1.5rem;
          padding-left: 1.5rem;
        }

        .video-modal-button {
          min-height: 10.375rem;
        }

        .video-modal-button .image-wrapper {
          width: 12rem;
          height: 10.375rem;
        }
      }
    </style>

    ${data.top_text == true ? topText : ``}
    
    <div class="video-modal-button">
      ${data.video}
    </div>
    <slot></slot>
  `;
  
  return template;
}

export class CustomiserVideoContent extends HTMLElement {
  private data: videoContent;
  private modal: CustomiserModal | null = null;
  private modalTrigger: HTMLButtonElement | null = null;
  private videoIframe: any;
  private videoPlayer: any;

  constructor() {
    super();
    
    const data = this.getAttribute('data');

    if (!data) {
      console.error('Need to supply some data for this option!', this);
      return;
    }

    this.data = JSON.parse(data)!;
    const shadowRoot = this.attachShadow({ mode: 'open' });

    const template = getMarkup(this.data);
    shadowRoot.appendChild(template.content.cloneNode(true));

    if (this.shadowRoot) {
      this.modal = this.querySelector('customiser-modal');
      this.modalTrigger = this.shadowRoot.querySelector('.video-modal-button');
      this.videoIframe = this.shadowRoot.querySelector('iframe');

      if (!this.modal || !this.videoIframe) {
        console.error('You need to render some modal content in here!', this);
      } else { 
        this.videoPlayer = new Vimeo(this.videoIframe);
        this.loadVideoData(this.videoPlayer, this.modalTrigger, this.data.title);
        
        this.modalTrigger?.addEventListener('click', (e) => {
          this.triggerModal();
        });
      }
    }
  }

  triggerModal() {
    this.modal?.setVisibility(true);
  }

  convertDurationToMinutes(seconds) {
    let minutes = Math.floor(seconds / 60);
    let remainingSeconds = seconds % 60;

    return minutes + ":" + (remainingSeconds < 10 ? '0' : '') + remainingSeconds.toFixed(0);
  }
  
  async loadVideoData(videoPlayer, modalTrigger, title) {
    try {
      let vidId = await videoPlayer.getVideoId();
      let vidThumbnail = `https://vumbnail.com/${vidId}.jpg`;
      let vidDuration = await videoPlayer.getDuration();
      let convertedDuration = this.convertDurationToMinutes(vidDuration);

      modalTrigger.innerHTML = `
        <div class="title-wrapper">
          <span>${title}</span>
          <span class="duration">${convertedDuration}</span>
        </div>
        <div class="image-wrapper">
          <img src="${vidThumbnail}"/>
          <span class="o-btn is-play"><svg class="" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"><path d="M13.15 6.665a.971.971 0 0 1 0 1.67L2.787 14.372c-.63.366-1.412-.097-1.412-.835V1.463c0-.738.783-1.201 1.412-.835L13.15 6.665Z" fill="#163021" stroke="#163021"></path></svg></span>
        </div>
      `;
    } catch (error) {
      console.error('Error:', error);
    }
  }

  buildVideoBlock(id, duration, imgSrc) {
    
  }
}

if (!customElements.get('customiser-video-content')) {
  customElements.define('customiser-video-content', CustomiserVideoContent);
}